import React, { useMemo } from 'react';
import { Box, Chip, Divider, Typography } from 'components/mui';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { format, parseISO } from 'date-fns';
import { useIsMobile } from 'hooks/useIsMobile';
import { LotusBlossomPurchase } from 'api/lotusBlossom/lotusBlossomAPI';
import { ClientMembership, summarisePurchases } from '../libs/clients';
import MembershipActions from './MembershipActions';

interface PurchaseHistoryProps {
	purchases: LotusBlossomPurchase[];
	/** Called with the membership as it stands after a cancel or resume. */
	onMembershipChange: (updated: LotusBlossomPurchase) => void;
}

/** One purchase as the grid holds it: raw values, so sorting stays correct. */
interface PurchaseRow {
	id: string;
	date: Date;
	description: string;
	status: string;
	/** Dollars, not Stripe's cents, so the column sorts and formats as money. */
	amount: number;
	currency: string;
}

/** Stripe reports amounts in the smallest currency unit. */
function formatAmount(amount: number, currency: string): string {
	return `$${amount.toFixed(2)} ${(currency || 'cad').toUpperCase()}`;
}

function describe(purchase: LotusBlossomPurchase): string {
	if (purchase.kind === 'subscription') return 'Monthly membership';
	return purchase.themeName || 'Single session';
}

function toRow(purchase: LotusBlossomPurchase, index: number): PurchaseRow {
	return {
		id: `${purchase.kind}-${purchase.date}-${index}`,
		date: parseISO(purchase.date),
		description: describe(purchase),
		status: purchase.status,
		amount: purchase.amount / 100,
		currency: purchase.currency,
	};
}

/** Green reads as "the money went through", which is the usual question. */
function statusColor(status: string): 'primary' | 'success' | 'default' {
	if (status === 'paid') return 'success';
	if (status === 'active' || status === 'trialing') return 'primary';
	return 'default';
}

/**
 * Plain wording for what Stripe will do next, because "cancel_at_period_end"
 * and a period timestamp are not an answer anyone can act on. Null when there
 * is nothing coming — an ended membership, or a backend that predates the
 * field.
 */
function describeRenewal(
	membership: ClientMembership | null
): { label: string; ending: boolean } | null {
	if (!membership?.periodEnd) return null;

	const on = format(parseISO(membership.periodEnd), 'MMM d, yyyy');

	return membership.cancelAtPeriodEnd
		? { label: `Cancelled — access ends ${on}`, ending: true }
		: { label: `Renews ${on}`, ending: false };
}

const columns: GridColDef[] = [
	{
		field: 'date',
		headerName: 'Date',
		type: 'date',
		flex: 0.9,
		minWidth: 130,
		// Held as a Date so the column sorts chronologically, not alphabetically.
		valueFormatter: ({ value }) =>
			value ? format(value as Date, 'MMM d, yyyy') : '',
	},
	{
		field: 'description',
		headerName: 'Purchase',
		flex: 1.6,
		minWidth: 180,
	},
	{
		field: 'status',
		headerName: 'Status',
		flex: 0.8,
		minWidth: 120,
		renderCell: (params: GridRenderCellParams<unknown, PurchaseRow>) =>
			params.row.status ? (
				<Chip
					label={params.row.status}
					size="small"
					color={statusColor(params.row.status)}
					variant="outlined"
				/>
			) : null,
	},
	{
		field: 'amount',
		headerName: 'Amount',
		type: 'number',
		flex: 0.8,
		minWidth: 130,
		// renderCell rather than a formatter, since the currency lives on the row.
		// `type: 'number'` still sorts on the raw amount and right-aligns.
		renderCell: (params: GridRenderCellParams<unknown, PurchaseRow>) => (
			<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
				{formatAmount(params.row.amount, params.row.currency)}
			</Typography>
		),
	},
];

function PurchaseHistory({ purchases, onMembershipChange }: PurchaseHistoryProps) {
	const isMobile = useIsMobile();
	const rows = useMemo(() => purchases.map(toRow), [purchases]);
	const summary = useMemo(() => summarisePurchases(purchases), [purchases]);
	const renewalNotice = useMemo(
		() => describeRenewal(summary.membership),
		[summary.membership]
	);

	if (purchases.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				No purchases recorded. Free enrolments and manually added clients don&apos;t
				appear here.
			</Typography>
		);
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					gap: { xs: 0.5, sm: 2 },
					alignItems: { sm: 'baseline' },
				}}
			>
				{summary.sessionCount > 0 && (
					<Typography variant="body2" sx={{ fontWeight: 700 }}>
						{summary.sessionCount} one-time session
						{summary.sessionCount === 1 ? '' : 's'}
					</Typography>
				)}

				{summary.membership && (
					<Typography variant="body2" color="text.secondary">
						Member since {format(parseISO(summary.membership.since), 'MMM yyyy')} (
						{summary.membership.status})
					</Typography>
				)}

				{/* The question this screen gets asked most: is another charge coming,
				    and when. */}
				{renewalNotice && (
					<Typography
						variant="body2"
						color={renewalNotice.ending ? 'warning.main' : 'text.primary'}
						sx={{ fontWeight: 600 }}
					>
						{renewalNotice.label}
					</Typography>
				)}

				{summary.membership && (
					<MembershipActions
						membership={summary.membership}
						onChange={onMembershipChange}
					/>
				)}
			</Box>

			<Divider />

			{/* A grid at this width would scroll sideways, so the phone keeps the
			    stacked list. */}
			{isMobile ? (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
					{rows.map((row) => (
						<Box
							key={row.id}
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'flex-start',
								gap: 1,
							}}
						>
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontWeight: 600 }}>
									{format(row.date, 'MMM d, yyyy')}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{row.description}
								</Typography>
								{row.status && (
									<Chip
										label={row.status}
										size="small"
										color={statusColor(row.status)}
										variant="outlined"
										sx={{ mt: 0.5 }}
									/>
								)}
							</Box>

							<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
								{formatAmount(row.amount, row.currency)}
							</Typography>
						</Box>
					))}
				</Box>
			) : (
				<DataGrid
					rows={rows}
					columns={columns}
					autoHeight
					density="compact"
					disableSelectionOnClick
					disableColumnMenu
					hideFooter
					initialState={{
						sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
					}}
					sx={{
						// Detail inside an expanded row, so it stays visually subordinate
						// to the client table around it.
						backgroundColor: 'background.paper',
						'& .MuiDataGrid-columnHeaders': { backgroundColor: 'action.hover' },
					}}
				/>
			)}
		</Box>
	);
}

export default PurchaseHistory;
